/* Shared building blocks. Every input has a visible <label>; hints and errors are tied to it with aria-describedby;
   form errors are announced with role="alert". */
import { useEffect, useRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';
import { Icon } from './icons';
import { Link } from './nav';
import { useT } from './i18n';
import { useDocumentTitle } from './hooks';
import { sanitizeSvg } from '../lib/svg';
import { passwordStrength } from '../lib/forms';
import { inlineParts } from '../content/legal';

export const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(' ');

/* ---------- page structure ---------- */
export function PageHeader({ title, lead, back, eyebrow }: { title: string; lead?: ReactNode; back?: { to: string; label: string }; eyebrow?: ReactNode }) {
  useDocumentTitle(title);
  return (
    <header className="page-head">
      {back && <Link to={back.to} className="back"><Icon.back />{back.label}</Link>}
      {eyebrow && <p className="page-eyebrow">{eyebrow}</p>}
      <h1 id="page-title" className="title" tabIndex={-1}>{title}</h1>
      {lead && <p className="lead">{lead}</p>}
    </header>
  );
}

export function Section({ title, id, foot, children, className }: { title?: ReactNode; id?: string; foot?: ReactNode; children: ReactNode; className?: string }) {
  const hid = id ? `${id}-title` : undefined;
  return (
    <section className={cx('section', className)} aria-labelledby={title ? hid : undefined} id={id}>
      {title && <h2 className="section__title" id={hid}>{title}</h2>}
      {children}
      {foot && <div className="section__foot">{foot}</div>}
    </section>
  );
}

export type Tone = 'teal' | 'blue' | 'orange' | 'green' | 'gray' | 'indigo' | 'red';

/* a row in an inset grouped list; a link when `to` (in-app) or `href` (other page) is given */
export function Row({ icon, tone = 'gray', title, sub, end, to, href, chevron, current }: {
  icon?: ReactNode; tone?: Tone; title: ReactNode; sub?: ReactNode; end?: ReactNode; to?: string; href?: string; chevron?: boolean; current?: boolean;
}) {
  const inner = (
    <>
      {icon && <span className={`tile tile--${tone}`}>{icon}</span>}
      <span className="row__main">
        <span className="row__title">{title}</span>
        {sub && <span className="row__sub">{sub}</span>}
      </span>
      {end && <span className="row__end">{end}</span>}
      {(to || href) && chevron !== false && <Icon.chevron />}
    </>
  );
  if (to) return <Link to={to} className="row row--link" aria-current={current ? 'page' : undefined}>{inner}</Link>;
  if (href) return <a href={href} className="row row--link">{inner}</a>;
  return <div className="row">{inner}</div>;
}

export function Chip({ tone, children, title }: { tone?: Tone; children: ReactNode; title?: string }) {
  return <span className={cx('chip', tone && `chip--${tone}`)} title={title}>{children}</span>;
}

/* the "AI" label on anything that uses AI (EU AI Act, Art. 50) */
export function AiTag({ full }: { full?: string }) {
  const { t } = useT();
  return <span className="chip chip--ai"><span aria-hidden="true">{t('ai.tag')}</span><span className="sr-only">{full ?? t('ai.tagFull')}</span></span>;
}

/* ---------- messages ---------- */
export function Alert({ tone = 'error', children, title }: { tone?: 'error' | 'success' | 'info' | 'warn'; children?: ReactNode; title?: ReactNode }) {
  const icon = tone === 'error' || tone === 'warn' ? <Icon.alert /> : tone === 'success' ? <Icon.ok /> : <Icon.info />;
  return (
    <div className={`alert alert--${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      {icon}
      <div className="alert__body">
        {title && <p className="alert__title">{title}</p>}
        {children && <div>{children}</div>}
      </div>
    </div>
  );
}

/* a polite live region that is always in the page, so changes are announced reliably */
export function Live({ children, className }: { children?: ReactNode; className?: string }) {
  return <div role="status" aria-live="polite" className={className}>{children}</div>;
}

export function Loading({ label }: { label?: string }) {
  const { t } = useT();
  return <div className="loading" role="status"><span className="spinner" aria-hidden="true" />{label ?? t('common.loading')}</div>;
}

export function LoadError({ message, retry }: { message: string; retry?: () => void }) {
  const { t } = useT();
  return (
    <Alert>
      {message}{' '}
      {retry && <button type="button" className="linkbtn" onClick={retry}>{t('common.retry')}</button>}
    </Alert>
  );
}

/* ---------- buttons ---------- */
type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'gray' | 'plain' | 'danger' | 'danger-solid'; busy?: boolean; block?: boolean; small?: boolean;
};
/* while busy the button stays focusable (aria-disabled) so keyboard focus isn't lost */
export function Button({ variant = 'primary', busy, block, small, className, children, onClick, type = 'button', ...rest }: BtnProps) {
  return (
    <button {...rest} type={type}
      className={cx('btn', `btn--${variant}`, block && 'btn--block', small && 'btn--small', className)}
      aria-disabled={busy || undefined} aria-busy={busy || undefined}
      onClick={e => { if (busy) { e.preventDefault(); return; } onClick?.(e); }}>
      {busy && <span className="spinner" aria-hidden="true" />}
      {children}
    </button>
  );
}

/* ---------- fields ---------- */
type A11y = { id: string; 'aria-describedby'?: string; 'aria-invalid'?: true };
export function Field({ id, label, hint, error, optional, children }: {
  id: string; label: ReactNode; hint?: ReactNode; error?: string | null; optional?: boolean; children: (a: A11y) => ReactNode;
}) {
  const { t } = useT();
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-error` : undefined;
  const describedBy = [errId, hintId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={cx('field', error && 'field--error')}>
      <label className="label" htmlFor={id}>{label}{optional && <span className="label__opt"> ({t('common.optional')})</span>}</label>
      {children({ id, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined })}
      {hint && <p className="hint" id={hintId}>{hint}</p>}
      {error && <p className="field-error" id={errId}><Icon.alert />{error}</p>}
    </div>
  );
}

type TextProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'id'> & {
  id: string; label: ReactNode; value: string; onChange: (v: string) => void; hint?: ReactNode; error?: string | null; optional?: boolean; ltr?: boolean;
};
export function TextField({ id, label, value, onChange, hint, error, optional, ltr, className, ...input }: TextProps) {
  return (
    <Field id={id} label={label} hint={hint} error={error} optional={optional}>
      {a => <input {...input} {...a} dir={ltr ? 'ltr' : input.dir} className={cx('input', ltr && 'input--ltr', className)}
        value={value} onChange={e => onChange(e.target.value)} />}
    </Field>
  );
}

export type Option = { value: string; label: string; lang?: string };
type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value' | 'id'> & {
  id: string; label: ReactNode; value: string; onChange: (v: string) => void; options: Option[]; placeholder?: string | false;
  hint?: ReactNode; error?: string | null; optional?: boolean;
};
export function SelectField({ id, label, value, onChange, options, placeholder, hint, error, optional, ...rest }: SelectProps) {
  const { t } = useT();
  return (
    <Field id={id} label={label} hint={hint} error={error} optional={optional}>
      {a => <Select {...rest} {...a} value={value} onChange={onChange} options={options} placeholder={placeholder === false ? false : (placeholder ?? t('common.choose'))} />}
    </Field>
  );
}

export function Select({ value, onChange, options, placeholder, className, ...rest }: Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> & {
  value: string; onChange: (v: string) => void; options: Option[]; placeholder?: string | false;
}) {
  return (
    <span className="select-wrap">
      <select {...rest} className={cx('select', className)} value={value} onChange={e => onChange(e.target.value)}>
        {placeholder !== false && <option value="">{placeholder}</option>}
        {options.map(o => <option key={o.value} value={o.value} lang={o.lang}>{o.label}</option>)}
      </select>
      <Icon.down />
    </span>
  );
}

export function Checkbox({ id, checked, onChange, label, description, error, required }: {
  id: string; checked: boolean; onChange: (v: boolean) => void; label: ReactNode; description?: ReactNode; error?: string | null; required?: boolean;
}) {
  const descId = description ? `${id}-desc` : undefined;
  const errId = error ? `${id}-error` : undefined;
  return (
    <div className={cx('check', error && 'check--error')}>
      <input type="checkbox" id={id} checked={checked} onChange={e => onChange(e.target.checked)}
        aria-describedby={[errId, descId].filter(Boolean).join(' ') || undefined} aria-invalid={error ? true : undefined} aria-required={required || undefined} />
      <div className="check__text">
        <label htmlFor={id}>{label}</label>
        {description && <p className="check__desc" id={descId}>{description}</p>}
        {error && <p className="field-error" id={errId}><Icon.alert />{error}</p>}
      </div>
    </div>
  );
}

/* password with a show/hide toggle and an optional strength hint */
export function PasswordField({ id, label, value, onChange, error, autoComplete, showStrength }: {
  id: string; label: string; value: string; onChange: (v: string) => void; error?: string | null; autoComplete: 'new-password' | 'current-password'; showStrength?: boolean;
}) {
  const { t } = useT();
  const [show, setShow] = useState(false);
  const strength = passwordStrength(value);
  const hint = showStrength ? (
    <>
      {t('pw.hint')}
      {value && <span className={`strength strength--${strength}`}> {t('pw.strength', { level: t(`pw.${strength}`) })}</span>}
    </>
  ) : undefined;
  return (
    <Field id={id} label={label} error={error} hint={hint}>
      {a => (
        <span className="password">
          <input {...a} className="input input--ltr" dir="ltr" type={show ? 'text' : 'password'} autoComplete={autoComplete}
            value={value} onChange={e => onChange(e.target.value)} minLength={autoComplete === 'new-password' ? 10 : undefined}
            autoCapitalize="none" spellCheck={false} />
          <button type="button" className="password__toggle" onClick={() => setShow(s => !s)} aria-controls={id} aria-pressed={show}
            aria-label={show ? t('common.hidePassword') : t('common.showPassword')}>
            {show ? <Icon.eyeOff /> : <Icon.eye />}<span aria-hidden="true">{show ? t('common.hide') : t('common.show')}</span>
          </button>
        </span>
      )}
    </Field>
  );
}

/* a 6-digit code; Arabic-Indic digits are converted by the caller (sixDigits) */
export function CodeField({ id, label, value, onChange, error, autoFocus }: {
  id: string; label: string; value: string; onChange: (v: string) => void; error?: string | null; autoFocus?: boolean;
}) {
  return (
    <TextField id={id} label={label} value={value} onChange={onChange} error={error} ltr className="input--code"
      inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" autoFocus={autoFocus} spellCheck={false} />
  );
}

/* ---------- copy to clipboard ---------- */
export function CopyButton({ text, label, selectRef }: { text: string; label: string; selectRef?: { current: HTMLElement | null } }) {
  const { t } = useT();
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const timer = useRef<number>(0);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const copy = async () => {
    window.clearTimeout(timer.current);
    try {
      await navigator.clipboard.writeText(text);
      setState('copied');
      timer.current = window.setTimeout(() => setState('idle'), 2500);
    } catch {
      setState('failed');
      const el = selectRef?.current;
      if (el) window.getSelection()?.selectAllChildren(el);
    }
  };
  return (
    <span className="copy">
      <Button variant="secondary" small onClick={copy}>{state === 'copied' ? <Icon.check /> : <Icon.copy />}{label}</Button>
      <Live className={state === 'failed' ? 'copy__msg' : 'sr-only'}>{state === 'copied' ? t('common.copied') : state === 'failed' ? t('common.copyFailed') : ''}</Live>
    </span>
  );
}

/* ---------- the QR code from the API, rebuilt safely (see lib/svg.ts) ---------- */
export function SafeSvg({ markup, label, className }: { markup: string; label: string; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const host = ref.current;
    if (!host) return;
    const svg = sanitizeSvg(markup);
    if (!svg) { setFailed(true); host.replaceChildren(); return; }
    setFailed(false);
    svg.setAttribute('aria-hidden', 'true');
    host.replaceChildren(svg);
  }, [markup]);
  return <div ref={ref} className={cx(className, failed && 'is-empty')} role="img" aria-label={label} />;
}

/* ---------- text with [placeholders] and {label|/path} links (legal pages) ---------- */
export function Inline({ s }: { s: string }) {
  return (
    <>
      {inlineParts(s).map((p, i) =>
        'text' in p ? <span key={i}>{p.text}</span>
          : 'placeholder' in p ? <mark key={i} className="ph">{p.placeholder}</mark>
            : p.href.startsWith('/') ? <Link key={i} to={p.href}>{p.link}</Link>
              : <a key={i} href={p.href} target="_blank" rel="noopener noreferrer">{p.link}</a>)}
    </>
  );
}

/* an external link that opens in a new tab and says so */
export function ExtLink({ href, children, className }: { href: string; children: ReactNode; className?: string }) {
  const { t } = useT();
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
      {children}<span className="sr-only"> {t('common.opensNew')}</span><Icon.external />
    </a>
  );
}
